/**
 * API endpoints, grouped by domain below.
 *
 * The axios instances, interceptors, token refresh and cache live in
 * apiClient.js and are re-exported from here.
 */
import axios from 'axios';
import {
  API_BASE_URL,
  REPORTS_SERVER_URL,
  api,
  handleRequest,
  ORG_ID_CHANGE_EVENT,
  AUTH_STATUS_EVENT,
  getCurrentOrgId,
  clearCache,
  refreshTokenManually,
} from './apiClient';

// Re-exported for components that import from '../services/api'.
export {
  ORG_ID_CHANGE_EVENT,
  AUTH_STATUS_EVENT,
  getCurrentOrgId,
  clearCache,
  refreshTokenManually,
};

export const login = async (credentials) => {
  try {
    const { email, password } = credentials;
    const response = await api.post('/auth/login', { email, password });

    // Store auth data in localStorage
    localStorage.setItem('token', response.token);
    localStorage.setItem('refreshToken', response.refreshToken); // Store refresh token
    localStorage.setItem('userId', response.uid);
    localStorage.setItem('userRoles', JSON.stringify(response.roles));
    if (response.displayName) {
      localStorage.setItem('displayName', response.displayName);
    }

    // Use default orgId if not provided
    const previousOrgId = localStorage.getItem('orgId');
    const newOrgId = response.orgId || '169';
    const timestamp = new Date().toISOString();

    // Track organization change in history if different
    if (previousOrgId !== newOrgId) {
      try {
        // Get existing history or initialize empty array
        const orgIdHistory = JSON.parse(localStorage.getItem('orgIdHistory') || '[]');

        // Add new entry to history (limit to last 10 entries)
        orgIdHistory.unshift({
          timestamp,
          previousOrgId,
          newOrgId,
          source: 'login'
        });

        // Keep only the last 10 entries
        if (orgIdHistory.length > 10) {
          orgIdHistory.length = 10;
        }

        // Save back to localStorage
        localStorage.setItem('orgIdHistory', JSON.stringify(orgIdHistory));
      } catch (e) {
        console.error('Error tracking orgId change during login:', e);
      }
    }

    localStorage.setItem('orgId', newOrgId);

    // Dispatch the org ID change event with full details
    window.dispatchEvent(new CustomEvent(ORG_ID_CHANGE_EVENT, {
      detail: {
        orgId: newOrgId,
        timestamp,
        previousOrgId,
        source: 'login'
      }
    }));

    return response;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const loginBasic = async (email, password) => {
  const basicAuth = btoa(`${email}:${password}`);
  const response = await api.post('/auth/login/basic', null, {
    headers: {
      Authorization: `Basic ${basicAuth}`
    }
  });
  const { token, refreshToken, uid, roles, displayName, photoURL } = response.data;

  // Store auth data in localStorage
  localStorage.setItem('token', token);
  localStorage.setItem('refreshToken', refreshToken); // Store refresh token
  localStorage.setItem('userId', uid);
  localStorage.setItem('userRoles', JSON.stringify(roles));
  localStorage.setItem('displayName', displayName);

  // Use default orgId if not provided
  const newOrgId = '169';
  localStorage.setItem('orgId', newOrgId);
  // Dispatch the org ID change event
  window.dispatchEvent(new CustomEvent(ORG_ID_CHANGE_EVENT, { detail: newOrgId }));

  return response.data;
};

export const logout = async () => {
  const response = await api.post('/auth/logout');
  clearUserRolesCache();
  // Clear auth-related items from localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken'); // Remove refresh token
  localStorage.removeItem('userId');
  localStorage.removeItem('userRoles');
  localStorage.removeItem('displayName');
  localStorage.removeItem('orgId');
  return response.data;
};

export const verifyAuth = async () => {
  const response = await api.get('/auth/verify');
  return response.data;
};

// New function to explicitly refresh the token
export const refreshToken = async () => {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  if (!refreshTokenValue) {
    console.error('No refresh token available in localStorage');
    throw new Error('No refresh token available');
  }

  try {
    console.log('Explicitly refreshing token...');
    const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
      refreshToken: refreshTokenValue
    });

    console.log('Refresh token response:', response.data);

    const { token, refreshToken: newRefreshToken, uid, roles, displayName } = response.data;

    if (!token || !newRefreshToken) {
      console.error('Missing token or refreshToken in response:', response.data);
      throw new Error('Invalid refresh token response');
    }

    // Store the new tokens
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', newRefreshToken);

    // Update other user info if available
    if (uid) localStorage.setItem('userId', uid);
    if (roles) localStorage.setItem('userRoles', JSON.stringify(roles));
    if (displayName) localStorage.setItem('displayName', displayName);

    console.log('Token refreshed successfully');

    return response.data;
  } catch (error) {
    console.error('Error refreshing token:', error);
    console.error('Error response:', error.response?.data || 'No response data');
    console.error('Error status:', error.response?.status || 'No status code');



    // Clear auth data after failed refresh
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRoles');
    localStorage.removeItem('displayName');

    // Dispatch event that authentication expired/failed
    window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
      detail: { status: 'expired' }
    }));

    throw error;
  }
};

export const updateOrganization = async (orgId) => {
  const previousOrgId = localStorage.getItem('orgId');
  const timestamp = new Date().toISOString();
  const newOrgId = orgId || '1066';

  // Roles are scoped to the active organisation, so the cached copy is stale
  // the moment it changes.
  clearUserRolesCache();

  // Track the change in localStorage history
  try {
    // Get existing history or initialize empty array
    const orgIdHistory = JSON.parse(localStorage.getItem('orgIdHistory') || '[]');

    // Add new entry to history (limit to last 10 entries)
    orgIdHistory.unshift({
      timestamp,
      previousOrgId,
      newOrgId,
      source: 'api_update'
    });

    // Keep only the last 10 entries
    if (orgIdHistory.length > 10) {
      orgIdHistory.length = 10;
    }

    // Save back to localStorage
    localStorage.setItem('orgIdHistory', JSON.stringify(orgIdHistory));
  } catch (e) {
    console.error('Error tracking orgId change in API:', e);
  }

  // Make the API call
  const response = await api.put('/auth/organization', { orgId: newOrgId });

  // Store in localStorage
  localStorage.setItem('orgId', newOrgId);

  // Ensure the API interceptors use the updated orgId for all future requests
  api.defaults.headers.common['x-organization-id'] = newOrgId;

  // Clear cached data as it may be org-specific
  clearCache();

  // Dispatch the change event with detailed info
  window.dispatchEvent(new CustomEvent(ORG_ID_CHANGE_EVENT, {
    detail: {
      orgId: newOrgId,
      timestamp,
      previousOrgId,
      source: 'api_update'
    }
  }));

  // Log the change
  console.log(`API: Organization ID changed from ${previousOrgId} to ${newOrgId} at ${timestamp}`);

  // Return the response data
  return response.data;
};

// MSSQL endpoints
export const getMssqlStatus = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get('/mssql/status');
    return response.data;
  } catch (error) {
    console.error('Error getting MSSQL status:', error);
    throw error;
  }
};

export const getCirclesByOrgId = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/circles`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/circles`);
      return response.data;
    } catch (error) {
      console.error('Error getting circles:', error);
      throw error;
    }
  });
};

export const getGradingSystemsByOrgId = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/grading-systems`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/grading-systems`);
      return response.data;
    } catch (error) {
      console.error('Error getting grading systems:', error);
      throw error;
    }
  });
};

export const getGradeHierarchy = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/grade-hierarchy`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/grade-hierarchy`);
      return response.data;
    } catch (error) {
      console.error('Error getting grade hierarchy:', error);
      throw error;
    }
  });
};

export const getOrganizationActiveGrades = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/active-grades`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/active-grades`);
      return response.data;
    } catch (error) {
      console.error('Error getting active grades:', error);
      throw error;
    }
  });
}

/**
 * Get active grades using LEGACY logic (employee-based) - only grades with current employees.
 * For KAM comparison/testing purposes.
 */
export async function getActiveGradesLegacy(orgId) {
  if (!orgId) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/active-grades-legacy`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/active-grades-legacy`);
      return response.data;
    } catch (error) {
      console.error('Error getting legacy active grades:', error);
      throw error;
    }
  });
};

// ============================================================
// Organization Settings API
// ============================================================

/**
 * Get organization settings (grade source preference, fallback settings)
 */
export async function getOrganizationSettings(orgId) {
  if (!orgId) {
    throw new Error('Organization ID required');
  }
  return handleRequest(`/organization-settings/${orgId}`, async () => {
    try {
      const response = await api.get(`/organization-settings/${orgId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting organization settings:', error);
      throw error;
    }
  });
}

/**
 * Update organization settings
 * @param {string} orgId - Organization ID
 * @param {Object} settings - Settings to update
 * @param {string} settings.gradeSourcePreference - 'permission_based', 'employee_based', or 'auto'
 * @param {boolean} settings.allowLegacyFallback - Whether to allow fallback to employee-based grades
 */
export async function updateOrganizationSettings(orgId, settings) {
  if (!orgId) {
    throw new Error('Organization ID required');
  }
  try {
    const response = await api.put(`/organization-settings/${orgId}`, settings);
    return response.data;
  } catch (error) {
    console.error('Error updating organization settings:', error);
    throw error;
  }
}

/**
 * Delete organization settings (revert to defaults)
 */
export async function deleteOrganizationSettings(orgId) {
  if (!orgId) {
    throw new Error('Organization ID required');
  }
  try {
    const response = await api.delete(`/organization-settings/${orgId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting organization settings:', error);
    throw error;
  }
}

export const getActiveGradesTranslation = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/active-grades-translation`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/active-grades-translation`);
      return response.data;
    } catch (error) {
      console.error('Error getting active grades translation:', error);
      throw error;
    }
  });
};

export const hasBaseGrades = async (orgId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/mssql/organization/${orgId}/has-base-grades`);
    return response.data;
  } catch (error) {
    console.error('Error checking if organization has base grades:', error);
    throw error;
  }
};


export const getCirclePercentileData = async (circleIds) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/mssql/circle/percentile', { circleIds });
    return response.data;
  } catch (error) {
    console.error('Error getting circle percentile data:', error);
    throw error;
  }
};

export const getGradePercentileData = async (circleIds) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/mssql/grade/percentile', { circleIds });
    return response.data;
  } catch (error) {
    console.error('Error getting grade percentile data:', error);
    throw error;
  }
};


export const verifyUser = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/mssql/verify-user', { userId });
    return response.data;
  } catch (error) {
    console.error('Error verifying user:', error);
    throw error;
  }
};

// Get uploaded employees for an organization
export const getUploadedEmployees = async (orgId) => {
  return handleRequest(`/mssql/organization/${orgId}/uploaded-employees`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/uploaded-employees`);
      return response.data;
    } catch (error) {
      console.error('Error fetching uploaded employees:', error);
      throw error;
    }
  });
};

// Get employee statistics including variance and delta
export const getEmployeeStatistics = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/employee-stats`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/employee-stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee statistics:', error);
      throw error;
    }
  });
};

// Get active grades mapped to Paterson Classic and Modern
export const getActiveGradesPatersonMapping = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/active-grades-paterson`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/active-grades-paterson`);
      return response.data;
    } catch (error) {
      console.error('Error fetching active grades Paterson mapping:', error);
      throw error;
    }
  });
};

// Get granted grades for an organization
export const getGrantedGrades = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/granted-grades`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/granted-grades`);
      return response.data;
    } catch (error) {
      console.error('Error fetching granted grades:', error);
      throw error;
    }
  });
};

// Get staging employees for an organization
export const getStagingEmployees = async (orgId) => {
  return handleRequest(`/mssql/organization/${orgId}/staging-employees`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/staging-employees`);
      return response.data;
    } catch (error) {
      console.error('Error fetching staging employees:', error);
      throw error;
    }
  });
};

// Get snapshot employee data for an organization
export const getSnapshotEmployeeData = async (orgId) => {
  return handleRequest(`/mssql/organization/${orgId}/snapshot-employee-data`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/snapshot-employee-data`);
      return response.data;
    } catch (error) {
      console.error('Error fetching snapshot employee data:', error);
      throw error;
    }
  });
};

// Get imported employees for an organization
export const getImportedEmployees = async (orgId) => {
  return handleRequest(`/mssql/organization/${orgId}/imported-employees`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/imported-employees`);
      return response.data;
    } catch (error) {
      console.error('Error fetching imported employees:', error);
      throw error;
    }
  });
};

// Get uploaded employees with LTI and STI amounts from PostgreSQL
export const getUploadedEmployeesWithAmounts = async (orgId) => {
  try {
    const response = await api.get(`/data-augmentation/organization/${orgId}/uploaded-employees`);
    return response.data;
  } catch (error) {
    console.error('Error fetching uploaded employees with amounts:', error);
    throw error;
  }
};

// Update LTI and STI amounts for a single employee
export const updateEmployeeAmounts = async (orgId, uploadedEmployeeId, ltiAmount, ontargetLtiAmount, employeeNumber) => {
  try {
    // Normalise null, undefined, empty string and numbers
    let ltiAmountValue = null;
    if (ltiAmount !== null && ltiAmount !== undefined && ltiAmount !== '') {
      const parsed = typeof ltiAmount === 'string' ? parseFloat(ltiAmount) : ltiAmount;
      ltiAmountValue = isNaN(parsed) ? null : parsed;
    }

    let ontargetLtiAmountValue = null;
    if (ontargetLtiAmount !== null && ontargetLtiAmount !== undefined && ontargetLtiAmount !== '') {
      const parsed = typeof ontargetLtiAmount === 'string' ? parseFloat(ontargetLtiAmount) : ontargetLtiAmount;
      ontargetLtiAmountValue = isNaN(parsed) ? null : parsed;
    }

    const response = await api.put(
      `/data-augmentation/organization/${orgId}/uploaded-employees/${uploadedEmployeeId}`,
      {
        ltiAmount: ltiAmountValue,
        ontargetLtiAmount: ontargetLtiAmountValue,
        employeeNumber: employeeNumber || '',
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating employee amounts:', error);
    throw error;
  }
};

// Helper function to retry bulk update with exponential backoff
const bulkUpdateWithRetry = async (orgId, updates, maxRetries, baseDelay) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await api.post(
        `/data-augmentation/organization/${orgId}/uploaded-employees/bulk-update`,
        { updates },
        {
          timeout: 300000, // 5 minutes timeout for bulk updates
        }
      );
      return response.data;
    } catch (error) {
      lastError = error;
      
      // Check if it's a connection error that we should retry
      const isConnectionError = 
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ETIMEDOUT' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('timeout') ||
        error.message?.includes('ECONNREFUSED') ||
        (error.response?.status >= 500 && error.response?.status < 600);
      
      // Don't retry on client errors (4xx) except 408 (timeout)
      if (!isConnectionError && error.response?.status !== 408) {
        throw error;
      }
      
      // If we've exhausted retries, throw
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Calculate exponential backoff delay
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`[BulkUpdate] Connection error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})...`, {
        code: error.code,
        message: error.message,
        status: error.response?.status
      });
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Helper function to process large updates in chunks
const bulkUpdateInChunks = async (orgId, updates, chunkSize, maxRetries, retryDelay, onProgress) => {
  const chunks = [];
  for (let i = 0; i < updates.length; i += chunkSize) {
    chunks.push(updates.slice(i, i + chunkSize));
  }
  
  console.log(`[BulkUpdate] Processing ${updates.length} updates in ${chunks.length} chunks of ${chunkSize}`);
  
  let totalUpdated = 0;
  let totalCreated = 0;
  const allErrors = [];
  
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    
    try {
      const result = await bulkUpdateWithRetry(orgId, chunk, maxRetries, retryDelay);
      
      totalUpdated += result.updated || 0;
      totalCreated += result.created || 0;
      if (result.errors) {
        allErrors.push(...result.errors);
      }
      
      // Report progress
      if (onProgress) {
        onProgress({
          processed: Math.min((i + 1) * chunkSize, updates.length),
          total: updates.length,
          chunk: i + 1,
          totalChunks: chunks.length,
          updated: totalUpdated,
          created: totalCreated,
          errors: allErrors.length
        });
      }
      
      console.log(`[BulkUpdate] Chunk ${i + 1}/${chunks.length} completed: ${result.updated} updated, ${result.created} created`);
      
      // Small delay between chunks to avoid overwhelming the server
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error(`[BulkUpdate] Chunk ${i + 1}/${chunks.length} failed:`, error);
      
      // Add chunk errors to allErrors
      chunk.forEach((update) => {
        allErrors.push({
          id: update.uploadedEmployeeId || update.snapshotEmployeeDataId || 0,
          error: `Chunk processing failed: ${error.message}`
        });
      });
      
      // Continue with next chunk instead of failing completely
      // This allows partial success
    }
  }
  
  return {
    updated: totalUpdated,
    created: totalCreated,
    errors: allErrors
  };
};

// Bulk update LTI and STI amounts for multiple employees
export const bulkUpdateEmployeeAmounts = async (orgId, updates, options = {}) => {
  const { 
    maxRetries = 3, 
    retryDelay = 2000,
    chunkSize = 500, // Process in chunks of 500
    onProgress 
  } = options;
  
  try {
    // Verify token exists before making request (same as individual update pattern)
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('[BulkUpdate] No token in localStorage before request');
      throw new Error('No authentication token found. Please log in again.');
    }
    
    console.log('[BulkUpdate] Making request with token present:', {
      tokenLength: token.length,
      orgId,
      updateCount: updates.length,
      chunkSize,
      willChunk: updates.length > chunkSize
    });
    
    // For large updates, process in chunks
    if (updates.length > chunkSize) {
      return await bulkUpdateInChunks(orgId, updates, chunkSize, maxRetries, retryDelay, onProgress);
    }
    
    // For smaller updates, use single request with retry
    return await bulkUpdateWithRetry(orgId, updates, maxRetries, retryDelay);
    
  } catch (error) {
    console.error('[BulkUpdate] Error bulk updating employee amounts:', {
      message: error.message,
      status: error.response?.status,
      code: error.code,
      hasToken: !!localStorage.getItem('token')
    });
    throw error;
  }
};

// Trigger sync from MSSQL to PostgreSQL for an organization
export const triggerDataSync = async (orgId) => {
  try {
    const response = await api.post(
      `/data-augmentation/organization/${orgId}/sync`,
      {},
      {
        timeout: 300000, // 5 minutes timeout for sync
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error triggering data sync:', error);
    throw error;
  }
};

// Roles are requested by several components as they mount (Reporting,
// ReportSections, DataAugmentation, EmbeddedApp), which produced duplicate
// requests on every page load. Share one in-flight request and hold the
// result briefly so concurrent mounts collapse into a single call.
let userRolesInFlight = null;
let userRolesCache = null;
const USER_ROLES_TTL_MS = 30000;

export const getUserRoles = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  if (userRolesCache && Date.now() - userRolesCache.at < USER_ROLES_TTL_MS) {
    return userRolesCache.data;
  }
  if (userRolesInFlight) {
    return userRolesInFlight;
  }

  userRolesInFlight = api
    .post('/mssql/user-roles')
    .then((response) => {
      userRolesCache = { data: response.data, at: Date.now() };
      return response.data;
    })
    .catch((error) => {
      console.error('Error getting user roles:', error);
      throw error;
    })
    .finally(() => {
      userRolesInFlight = null;
    });

  return userRolesInFlight;
};

/** Drop the cached roles. Call on logout or when the active org changes. */
export const clearUserRolesCache = () => {
  userRolesCache = null;
  userRolesInFlight = null;
};


// Questionnaire endpoints
export const createQuestionnaire = async (data) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/questionnaires', {
      ...data,
      organizationId: getCurrentOrgId(),
      createdBy: getCurrentUserId(),
      updatedBy: getCurrentUserId()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating questionnaire:', error);
    throw error;
  }
};

export const createQuestionnaireRaw = async (data) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/questionnaires/raw', {
      ...data,
      organizationId: parseInt(getCurrentOrgId()),
      createdBy: getCurrentUserId(),
      updatedBy: getCurrentUserId()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating questionnaire from raw data:', error);
    throw error;
  }
};

export const getQuestionnaires = () => {
  return handleRequest('questionnaires', () =>
    api.get('/questionnaires')
  );
};

export const getQuestionnairesByOrganization = async () => {
  const orgId = getCurrentOrgId();
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/questionnaires/organization?organizationId=${orgId}`, async () => {
    try {
      const response = await api.get(`/questionnaires/organization?organizationId=${orgId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
      throw error;
    }
  });
};

export const getQuestionnaireById = async (id) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/questionnaires/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching questionnaire:', error);
    throw error;
  }
};

export const updateQuestionnaire = async (id, data) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.patch(`/questionnaires/${id}`, {
      ...data,
      updatedBy: getCurrentUserId()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating questionnaire:', error);
    throw error;
  }
};

export const updateQuestionnaireRaw = async (id, data) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.patch(`/questionnaires/${id}/raw`, {
      ...data,
      organizationId: parseInt(getCurrentOrgId()),
      updatedBy: getCurrentUserId(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating questionnaire from raw data:', error);
    throw error;
  }
};

export const getQuestionnaireAnswers = async (id) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/questionnaires/${id}/answers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching questionnaire answers:', error);
    throw error;
  }
};

export const deleteQuestionnaire = async (id) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    await api.delete(`/questionnaires/${id}`);
  } catch (error) {
    console.error('Error deleting questionnaire:', error);
    throw error;
  }
};

// Report APIs
export const generateReport = async (reportData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/reports/generate', reportData);
    return response.data;
  } catch (error) {
    console.error('Error generating report:', error);
    throw error;
  }
};

export const getReportsByOrganization = async (orgId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/reports/organization/${orgId}`);
    return response.data?.reports || [];
  } catch (error) {
    console.error('Error fetching reports:', error);
    throw error;
  }
};

export const getReportsUsageSummary = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get('/reports/usage-summary');
    return response.data || [];
  } catch (error) {
    console.error('Error fetching reports usage summary:', error);
    throw error;
  }
};

export const getOrganizationNames = async (orgIds) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const ids = Array.isArray(orgIds) ? orgIds : [];
    if (ids.length === 0) return {};
    const response = await api.post('/mssql/organization-names', { orgIds: ids });
    return response.data || {};
  } catch (error) {
    console.error('Error fetching organization names:', error);
    throw error;
  }
};

export const getReportById = async (reportId) => {
  try { 
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/reports/${reportId}`);
    return response?.data || {};
  } catch (error) {
    console.error('Error fetching report:', error);
    throw error;
  }
};

export const getReportStatus = async (reportId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/reports/${reportId}/status`);
    return {
      status: response.data?.jobStatus?.status || 'UNKNOWN',
      progress: response.data?.jobStatus?.progress || 0,
      message: response.data?.jobStatus?.message || ''
    };
  } catch (error) {
    console.error('Error fetching report status:', error);
    throw error;
  }
};

export const deleteReport = async (reportId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.delete(`/reports/${reportId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting report:', error);
    throw error;
  }
};

// Create separate axios instance for reports server
const reportsApi = axios.create({
  baseURL: REPORTS_SERVER_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Organization-ID': getCurrentOrgId()
  },
});

// Add auth interceptor for reports API
reportsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('orgId') || '1066';
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Reports API - Token added to request:', token.substring(0, 20) + '...');
    } else {
      console.warn('Reports API - No token found in localStorage');
      delete config.headers.Authorization;
    }
    config.headers['X-Organization-ID'] = orgId;
    console.log('Reports API - Request headers:', {
      hasAuthorization: !!config.headers.Authorization,
      'X-Organization-ID': config.headers['X-Organization-ID'],
      url: config.url
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Export report to PDF
export const exportReportToPDF = async (reportId, reportData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const url = `/report-server/reports/${reportId}/export/pdf`;
    console.log('Exporting PDF to:', REPORTS_SERVER_URL + url);
    console.log('Report ID:', reportId);
    const response = await reportsApi.post(
      url,
      reportData,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    // Verify response is a PDF blob
    if (!(response.data instanceof Blob)) {
      throw new Error('Invalid response: expected PDF blob');
    }
    
    // Check content type
    const contentType = response.headers['content-type'] || response.data.type;
    if (contentType && !contentType.includes('application/pdf')) {
      console.warn(`Unexpected content type: ${contentType}, expected application/pdf`);
    }
    
    // Create blob URL with explicit PDF MIME type
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Return blob URL for modal preview
    // The caller will handle displaying it in a modal
    return blobUrl;
  } catch (error) {
    console.error('Error exporting report to PDF:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to export report to PDF';
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: errorMessage
    });
    throw new Error(errorMessage);
  }
};

// Export report to Excel
export const exportReportToExcel = async (reportId, reportData, isKAMUser = false, circleName = null, gradingSystemName = null) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const url = `/report-server/reports/${reportId}/export/excel`;
    console.log('Exporting Excel to:', REPORTS_SERVER_URL + url);
    console.log('Report ID:', reportId);
    const response = await reportsApi.post(
      url,
      { ...reportData, isKAMUser, circleName, gradingSystemName },
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', `report-${reportId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting report to Excel:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to export report to Excel';
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: errorMessage
    });
    throw new Error(errorMessage);
  }
};

// Export processed data to Excel
export const exportProcessedDataToExcel = async (reportId, metadata, circleId, gradingSystemId, organizationId, isKAMUser = false, circleName = null, gradingSystemName = null) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const url = `/report-server/reports/${reportId}/export/processed-data/excel`;
    console.log('Exporting processed data Excel to:', REPORTS_SERVER_URL + url);
    console.log('Report ID:', reportId);
    
    const response = await reportsApi.post(
      url,
      {
        metadata,
        circleId,
        gradingSystemId,
        organizationId,
        isKAMUser,
        circleName,
        gradingSystemName
      },
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `processed-data-${reportId}-${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting processed data to Excel:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to export processed data to Excel';
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: errorMessage
    });
    throw new Error(errorMessage);
  }
};

// Export percentile source data to CSV (all grades)
export const exportPercentileSourceDataToExcel = async (reportId, circleId, circleName, gradingSystemId, organizationId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    // Call backend CSV endpoint directly (no reports server needed)
    const url = `/data-augmentation/percentile-source-data/csv?circleId=${circleId || ''}&requestingOrganizationId=${organizationId}`;
    console.log('Exporting percentile source data CSV to:', API_BASE_URL + url);
    console.log('Report ID:', reportId);
    
    const response = await api.get(url, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { 
      type: 'text/csv;charset=utf-8;' 
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `percentile-source-data-${reportId}-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting percentile source data to CSV:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to export percentile source data to CSV';
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: errorMessage
    });
    throw new Error(errorMessage);
  }
};

/**
 * Download percentile source data CSV for a single grade (KAM per-grade export).
 * @param {string} reportId - Report ID (for filename)
 * @param {string} circleId - Circle ID
 * @param {string} organizationId - Requesting organization ID
 * @param {string} gradeName - Grade name to filter (e.g. "A", "B Lower")
 * @param {string} [metric] - Metric field key (tgp, ltiAmount, ontargetLtiAmount, sti, onTargetSti). When set, CSV contains only rows that contributed to that metric's sample so count matches Market Sample.
 */
export const downloadPercentileSourceDataCsvForGrade = async (reportId, circleId, organizationId, gradeName, metric) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const encodedGrade = encodeURIComponent(gradeName || '');
    const metricParam = metric ? `&metric=${encodeURIComponent(metric)}` : '';
    const url = `/data-augmentation/percentile-source-data/csv?circleId=${circleId || ''}&requestingOrganizationId=${organizationId}&gradeName=${encodedGrade}${metricParam}`;
    const response = await api.get(url, {
      responseType: 'blob',
      headers: { 'Content-Type': 'application/json' },
    });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const safeGrade = (gradeName || 'grade').replace(/[/\\?*:"]/g, '-');
    link.setAttribute('download', `percentile-source-data-${reportId}-${safeGrade}-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    return { success: true };
  } catch (error) {
    console.error('Error downloading percentile source data CSV for grade:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to download CSV for grade';
    throw new Error(errorMessage);
  }
};

// Retranslate report grades
export const retranslateReportGrades = async (reportId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post(`/reports/${reportId}/retranslate`);
    return response?.data || {};
  } catch (error) {
    console.error('Error retranslating report grades:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to retranslate report grades';
    throw new Error(errorMessage);
  }
};

// Recalculate percentiles for an existing report
export const recalculatePercentiles = async (reportId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post(`/reports/${reportId}/recalculate-percentiles`);
    return response?.data || {};
  } catch (error) {
    console.error('Error recalculating percentiles:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to recalculate percentiles';
    throw new Error(errorMessage);
  }
};

// Helper functions for analysis - Using the new report APIs
export const analyzeCircleWithGradingSystem = async (circleId, gradingSystemId) => {
  try {
    const orgId = getCurrentOrgId();
    console.log(`Creating report for circleId: ${circleId}, gradingSystemId: ${gradingSystemId}`);

    const reportData = {
      name: `Analysis Report - ${new Date().toLocaleString()}`,
      description: `Analysis for Circle ${circleId} using Grading System ${gradingSystemId}`,
      circleId: circleId,
      gradingSystemId: gradingSystemId,
      organizationId: orgId
    };

    const response = await generateReport(reportData);
    console.log('Report generation response:', response);

    if (response?.success && response?.reportId) {
      return {
        jobId: response.reportId, // Map reportId to jobId for backward compatibility
        status: 'PENDING',
        createdAt: new Date().toISOString()
      };
    }

    throw new Error('Invalid response format - no report ID returned');
  } catch (error) {
    console.error(`Error creating report with circleId: ${circleId}, gradingSystemId: ${gradingSystemId}:`, error);
    throw error;
  }
};

export const getJobStatus = async (jobId) => {
  try {
    const response = await getReportStatus(jobId);
    console.log('Report status response:', response);

    return {
      status: response.status,
      progress: response.progress || 0,
      message: response.message || ''
    };
  } catch (error) {
    console.error(`Error fetching status for report ${jobId}:`, error);
    throw error;
  }
};

export const getJobResult = async (jobId) => {
  try {
    const response = await getReportById(jobId);
    console.log('Report result response:', response);

    return response;
  } catch (error) {
    console.error(`Error fetching result for report ${jobId}:`, error);
    throw error;
  }
};

// Helper functions for authentication
// const getAuthToken = () => {
//   return localStorage.getItem('token');
// };

const getCurrentUserId = () => {
  return localStorage.getItem('userId');
};

// Get circles for an organization
export const getCircles = async (orgId) => {
  try {
    return await getCirclesByOrgId(orgId);
  } catch (error) {
    console.error('Error fetching circles:', error);
    throw error;
  }
};

// Get grading systems for an organization
export const getGradingSystems = async (orgId) => {
  try {
    return await getGradingSystemsByOrgId(orgId);
  } catch (error) {
    console.error('Error fetching grading systems:', error);
    throw error;
  }
};

// Create a new report
export const createReport = async (reportData) => {
  try {
    // Extract circleId and gradingSystemId from the report data
    const { circleIds, gradingSystemId } = reportData;

    // For now, we'll use the first circle ID if multiple are provided
    const circleId = Array.isArray(circleIds) && circleIds.length > 0
      ? circleIds[0]
      : reportData.circleId;

    if (!circleId || !gradingSystemId) {
      throw new Error('Circle ID and Grading System ID are required');
    }

    // Use the correct endpoint by delegating to analyzeCircleWithGradingSystem
    return await analyzeCircleWithGradingSystem(circleId, gradingSystemId);
  } catch (error) {
    console.error('Error creating report:', error);
    throw error;
  }
};

export default api;